import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "./styles/globals.css";
import { CookieBanner } from "./components/CookieBanner";
import { loadGtmIfConsented } from "./lib/analytics";

loadGtmIfConsented();

import Home from "./routes/Home";
import Faq from "./routes/Faq";
import HowItWorks from "./routes/HowItWorks";
import Pricing from "./routes/Pricing";
import AuditNew from "./routes/AuditNew";
import AuditResults from "./routes/AuditResults";
import ReviewChecker from "./routes/ReviewChecker";
import Demo from "./routes/Demo";
import AdminProspectNew from "./routes/AdminProspectNew";
import AdminProspectList from "./routes/AdminProspectList";
import StartTrial from "./routes/StartTrial";
import ManualScraperPortal from "./routes/ManualScraperPortal";
import ManualScraperLogin from "./routes/ManualScraperLogin";
import ManualScraperExtension from "./routes/ManualScraperExtension";
import ManualScraperVerify from "./routes/ManualScraperVerify";
import ManualScraperScrapeSubmit from "./routes/ManualScraperScrapeSubmit";
import AdminLogin from "./routes/AdminLogin";
import AdminHome from "./routes/AdminHome";
import AdminList from "./routes/AdminList";
import AdminDetail from "./routes/AdminDetail";
import AdminAgentRuns from "./routes/AdminAgentRuns";
import AdminAgentRunDetail from "./routes/AdminAgentRunDetail";
import AdminCustomers from "./routes/AdminCustomers";
import AdminCustomerDetail from "./routes/AdminCustomerDetail";
import AdminProductDetail from "./routes/AdminProductDetail";
import AdminProductReviews from "./routes/AdminProductReviews";
import AdminQueue from "./routes/AdminQueue";
import AdminManualScrapers from "./routes/AdminManualScrapers";
import AdminScrapes from "./routes/AdminScrapes";
import AdminScrapeDetail from "./routes/AdminScrapeDetail";
import AdminRetailAccounts from "./routes/AdminRetailAccounts";
import AdminUnitEconomics from "./routes/AdminUnitEconomics";
import CustomerLogin from "./routes/CustomerLogin";
import CustomerDashboard from "./routes/CustomerDashboard";
import DashboardOverview from "./routes/DashboardOverview";
import DashboardBrandNew from "./routes/DashboardBrandNew";
import DashboardBrandEdit from "./routes/DashboardBrandEdit";
import DashboardBrands from "./routes/DashboardBrands";
import DashboardReviews from "./routes/DashboardReviews";
import DashboardTakedowns from "./routes/DashboardTakedowns";
import DashboardAudits from "./routes/DashboardAudits";
import DashboardProducts from "./routes/DashboardProducts";
import DashboardProductDetail from "./routes/DashboardProductDetail";
import DashboardReports from "./routes/DashboardReports";
import DashboardNotifications from "./routes/DashboardNotifications";
import DashboardTeam from "./routes/DashboardTeam";
import DashboardBilling from "./routes/DashboardBilling";
import CustomerAuditDetail from "./routes/CustomerAuditDetail";
import CustomerOnboarding from "./routes/CustomerOnboarding";
import CheckoutSubscribe from "./routes/CheckoutSubscribe";
import AuthCheckoutSuccess from "./routes/AuthCheckoutSuccess";
import Terms from "./routes/Terms";
import Privacy from "./routes/Privacy";
import NotFound from "./routes/NotFound";

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/audit" element={<AuditNew />} />
        <Route path="/audit/:id" element={<AuditResults />} />
        <Route path="/review-checker" element={<ReviewChecker />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/start-trial" element={<StartTrial />} />
        <Route path="/admin/prospects" element={<AdminProspectList />} />
        <Route path="/admin/prospects/new" element={<AdminProspectNew />} />
        <Route path="/manual-scrape" element={<ManualScraperPortal />} />
        <Route path="/manual-scrape/login" element={<ManualScraperLogin />} />
        <Route path="/manual-scrape/verify" element={<ManualScraperVerify />} />
        <Route path="/manual-scrape/extension" element={<ManualScraperExtension />} />
        <Route path="/manual-scrape/scrapes/:id" element={<ManualScraperScrapeSubmit />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminHome />} />
        <Route path="/admin/audits" element={<AdminList />} />
        <Route path="/admin/audits/:id" element={<AdminDetail />} />
        <Route path="/admin/customers" element={<AdminCustomers />} />
        <Route path="/admin/customers/:id" element={<AdminCustomerDetail />} />
        <Route path="/admin/products/:id" element={<AdminProductDetail />} />
        <Route path="/admin/products/:id/reviews" element={<AdminProductReviews />} />
        <Route path="/admin/queue" element={<AdminQueue />} />
        <Route path="/admin/agent-runs" element={<AdminAgentRuns />} />
        <Route path="/admin/agent-runs/:id" element={<AdminAgentRunDetail />} />
        <Route path="/admin/manual-scrapers" element={<AdminManualScrapers />} />
        <Route path="/admin/scrapes" element={<AdminScrapes />} />
        <Route path="/admin/scrapes/:id" element={<AdminScrapeDetail />} />
        <Route path="/admin/retail-accounts" element={<AdminRetailAccounts />} />
        <Route path="/admin/unit-economics" element={<AdminUnitEconomics />} />
        <Route path="/login" element={<CustomerLogin />} />
        <Route path="/dashboard" element={<DashboardOverview />} />
        <Route path="/dashboard/legacy" element={<CustomerDashboard />} />
        <Route path="/dashboard/brands" element={<DashboardBrands />} />
        <Route path="/dashboard/brands/new" element={<DashboardBrandNew />} />
        <Route path="/dashboard/brands/:id/edit" element={<DashboardBrandEdit />} />
        <Route path="/dashboard/reviews" element={<DashboardReviews />} />
        <Route path="/dashboard/takedowns" element={<DashboardTakedowns />} />
        <Route path="/dashboard/audits" element={<DashboardAudits />} />
        <Route path="/dashboard/products" element={<DashboardProducts />} />
        <Route path="/dashboard/products/:asin" element={<DashboardProductDetail />} />
        <Route path="/dashboard/reports" element={<DashboardReports />} />
        <Route path="/dashboard/team" element={<DashboardTeam />} />
        <Route path="/dashboard/billing" element={<DashboardBilling />} />
        <Route path="/dashboard/notifications" element={<DashboardNotifications />} />
        <Route path="/dashboard/onboarding" element={<CustomerOnboarding />} />
        <Route path="/dashboard/audits/:id" element={<CustomerAuditDetail />} />
        <Route path="/checkout/subscribe" element={<CheckoutSubscribe />} />
        <Route path="/auth/checkout-success" element={<AuthCheckoutSuccess />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <CookieBanner />
    </BrowserRouter>
  </StrictMode>
);
